import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FinanceScopeProvider } from "../contexts/FinanceScopeContext";
import { Button } from "./ui/button";
import { ArrowLeft, BarChart3, Coins, Banknote, BookOpen, Percent, BadgeCheck, FileText, ShieldCheck } from "lucide-react";
import HeaderTools from "./HeaderTools";
import Breadcrumbs from "./Breadcrumbs";
import UserMenu from "./UserMenu";
import AuthedShellChrome from "./AuthedShellChrome";

const FINANCE_ALLOWED = new Set([
    "kaafi_system_admin", "kaafi_hr_manager", "asl_hr_officer", "asl_senior_management",
    "asl_finance_viewer", "asl_read_only_viewer",
]);

const NAV = [
    { key: "dashboard", label: "Master Finance", icon: BarChart3, path: "/master-finance", end: true },
    { key: "payroll", label: "Payroll", icon: Coins, path: "/master-finance/payroll" },
    { key: "funding", label: "Payroll Funding", icon: Banknote, path: "/master-finance/funding" },
    { key: "ledger", label: "Consolidated Ledger", icon: BookOpen, path: "/master-finance/ledger" },
    { key: "fees", label: "Management Fees", icon: Percent, path: "/master-finance/management-fees" },
    { key: "wage-rates", label: "Wage Rates Overview", icon: BadgeCheck, path: "/master-finance/wage-rates" },
    { key: "reports", label: "Finance Reports", icon: FileText, path: "/master-finance/reports" },
    { key: "audit", label: "Audit (Finance)", icon: ShieldCheck, path: "/master-finance/audit" },
];

function initials(name = "") {
    // unused after switching to UserMenu; retained for backwards-compat callers
    return name.split(" ").filter(Boolean).map(s => s[0]?.toUpperCase()).slice(0, 2).join("");
}
// eslint-disable-next-line no-unused-vars
const _initials = initials;

export default function MasterFinanceShell({ title, children }) {
    const { user } = useAuth();
    const navigate = useNavigate();

    if (!user || !FINANCE_ALLOWED.has(user.role)) {
        return (
            <div className="min-h-screen flex items-center justify-center p-8 bg-[color:var(--asl-bg)]" data-testid="mf-forbidden">
                <div className="asl-card p-8 max-w-md text-center">
                    <ShieldCheck className="h-10 w-10 mx-auto asl-crimson-text mb-3" />
                    <h2 className="text-lg font-bold asl-navy-text">Master Finance is not available for your role</h2>
                    <p className="text-sm text-[color:var(--asl-muted)] mt-2">Department managers see only their own module workspace.</p>
                    <Button className="mt-4" onClick={() => navigate("/")} data-testid="mf-back-home">Go back to Home</Button>
                </div>
            </div>
        );
    }

    return (
        <FinanceScopeProvider>
            <div className="min-h-screen flex" data-testid="master-finance-shell">
                <aside className="asl-sidebar" data-testid="mf-sidebar">
                    <div className="px-5 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                        <button onClick={() => navigate("/")} className="text-xs text-white/70 hover:text-white flex items-center gap-1 mb-3"
                            data-testid="mf-back-home-link">
                            <ArrowLeft className="h-3 w-3" /> Back to Home
                        </button>
                        <div className="flex items-center gap-3">
                            <img src="/asl-logo.png" alt="ASL" style={{ height: 36, width: "auto" }} className="asl-logo--washed" />
                            <div className="leading-tight">
                                <div className="text-white font-bold tracking-wide text-base">ASL HR Platform</div>
                                <div className="text-[10px] font-bold tracking-[2px] asl-crimson-text" style={{ color: "var(--asl-crimson)" }} data-testid="mf-sidebar-label">
                                    MASTER FINANCE
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav className="flex flex-col gap-1 px-3 py-4">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.key}
                                to={item.path}
                                end={item.end}
                                data-testid={`mf-nav-${item.key}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 px-3 py-2 rounded text-sm transition ${isActive ? "bg-white/15 text-white" : "text-white/75 hover:bg-white/10 hover:text-white"}`
                                }
                            >
                                <item.icon className="h-4 w-4" />
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </aside>

                <div className="flex-1 flex flex-col">
                    <header className="flex items-center justify-between px-8 py-4 bg-white"
                        style={{ borderBottom: "1px solid var(--asl-border)" }}>
                        <h1 className="text-xl font-bold asl-navy-text" data-testid="mf-page-title">{title || "Master Finance"}</h1>
                        <div className="flex items-center gap-3">
                            <HeaderTools />
                            <UserMenu />
                        </div>
                    </header>
                    <Breadcrumbs />
                    <main className="flex-1 p-8 overflow-auto">{children}</main>
                </div>
                <AuthedShellChrome />
            </div>
        </FinanceScopeProvider>
    );
}
